import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import type { IFormElement } from '../types';
import { useFormState } from '../contexts/FormStateContext';
import styles from './fields.module.css';

export interface RatingFieldProps {
  element: IFormElement;
  questionNumber?: number;
}

export const RatingField: React.FC<RatingFieldProps> = ({
  element,
  questionNumber,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const formState = useFormState();
  const error = errors[element.name];
  const validationError = formState.validationErrors[element.name];

  const rateMin = element.rateMin ?? 1;
  const rateMax = element.rateMax ?? 5;
  const rateValues: number[] = [];
  for (let i = rateMin; i <= rateMax; i++) {
    rateValues.push(i);
  }

  const renderLabel = () => {
    if (!element.title) return null;
    return (
      <label
        className={`${styles.label} ${
          element.isRequired ? styles.required : ''
        }`}
      >
        {questionNumber && (
          <span className={styles.questionNumber}>{questionNumber}. </span>
        )}
        {element.title}
      </label>
    );
  };

  return (
    <div className={styles.formField}>
      {renderLabel()}
      {element.description && (
        <p className={styles.description}>{element.description}</p>
      )}
      <Controller
        name={element.name}
        control={control}
        defaultValue={element.defaultValue}
        render={({ field }) => (
          <div className={styles.ratingContainer}>
            {rateValues.map((value) => (
              <button
                key={value}
                type="button"
                className={`${styles.ratingItem} ${
                  field.value === value ? styles.selected : ''
                }`}
                onClick={() => {
                  field.onChange(value);
                  formState.markFieldAsTouched(element.name);
                  formState.clearValidationError(element.name);
                }}
                disabled={element.readOnly}
                aria-label={`Nota ${value}`}
              >
                {value}
              </button>
            ))}
          </div>
        )}
      />
      {element.minRateDescription && element.maxRateDescription && (
        <div className={styles.rateDescriptions}>
          <span>{element.minRateDescription}</span>
          <span>{element.maxRateDescription}</span>
        </div>
      )}

      {element.showCommentArea && (
        <div className={styles.commentArea}>
          <label className={styles.commentAreaLabel}>
            {element.commentText || 'Comentário adicional'}
          </label>
          <Controller
            name={`${element.name}_comment`}
            control={control}
            defaultValue=""
            render={({ field }) => (
              <textarea
                {...field}
                className={`${styles.input} ${styles.textarea}`}
                placeholder={
                  element.commentPlaceholder || 'Digite seu comentário'
                }
                rows={3}
              />
            )}
          />
        </div>
      )}

      {(error || validationError) && (
        <p className={styles.errorMessage}>
          {(error?.message as string) || validationError}
        </p>
      )}
    </div>
  );
};

export default RatingField;
